import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { observer } from "mobx-react";
import useStore from "../helpers/useStore";
import { DoctorContext } from "../stores/Doctor/doctor.provider";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export type MigrationType = "doctor" | "clinic";

interface IdMigrationDialogProps {
  open: boolean;
  onClose: () => void;
  /** "doctor" → LANR update, "clinic" → BSNR update */
  type: MigrationType;
  /** The doctor/clinic UUID (PK) */
  entityId: string;
  /** Current LANR or BSNR value (null if not yet assigned) */
  currentValue?: string | null;
  /** Called after a successful update with the new value */
  onMigrationComplete?: (newValue: string) => void;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const IdMigrationDialog: React.FC<IdMigrationDialogProps> = observer(
  ({ open, onClose, type, entityId, currentValue, onMigrationComplete }) => {
    const doctorStore = useStore(DoctorContext);

    const isDoctor = type === "doctor";
    const idLabel = isDoctor ? "LANR" : "BSNR";
    const entityLabel = isDoctor ? "Arzt" : "Praxis";

    const title = currentValue
      ? `${idLabel} ändern`
      : `${idLabel} hinzufügen`;

    const [newValue, setNewValue] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    // Preflight: count of affected Rxs (loaded when dialog opens for an existing LANR/BSNR)
    const [affectedCount, setAffectedCount] = useState<number | null>(null);
    const [loadingCount, setLoadingCount] = useState(false);
    // When changing an existing value, the input stays locked until the user explicitly unlocks it
    const [unlocked, setUnlocked] = useState(false);

    const isChange = !!currentValue;

    // Reset state when dialog opens/closes
    useEffect(() => {
      if (open) {
        setNewValue(currentValue || "");
        setSaving(false);
        setError(null);
        setSuccess(false);
        setUnlocked(!isChange); // adding (no current value) → input editable immediately
        setAffectedCount(null);

        // Preflight count only matters when changing an existing value
        if (isChange && entityId) {
          setLoadingCount(true);
          (async () => {
            try {
              const count = isDoctor
                ? await doctorStore.countAffectedRxsForDoctor(entityId)
                : await doctorStore.countAffectedRxsForClinic(entityId);
              setAffectedCount(count);
            } catch {
              setAffectedCount(null);
            } finally {
              setLoadingCount(false);
            }
          })();
        }
      }
    }, [open, currentValue, isChange, isDoctor, entityId, doctorStore]);

    // Valid if: 9 digits and different from current
    const trimmed = newValue.trim();
    const isValid = trimmed.length === 9 && trimmed !== (currentValue || "").trim();

    const handleSave = async () => {
      if (!isValid) return;
      const valueToSave = trimmed;

      setSaving(true);
      setError(null);
      try {
        if (isDoctor) {
          await doctorStore.updateDoctorLANR(entityId, valueToSave);
        } else {
          await doctorStore.updateClinicBSNR(entityId, valueToSave);
        }
        setSuccess(true);
        onMigrationComplete?.(valueToSave);
        // Auto-close after successful save
        setTimeout(() => onClose(), 500);
      } catch (err: any) {
        const rawMsg =
          err?.response?.data?.message || err?.message || "Speichern fehlgeschlagen";
        const msg = rawMsg.replace(/^\[Error ID: [^\]]+\]\s*-\s*/, "");
        setError(msg);
      } finally {
        setSaving(false);
      }
    };

    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{title}</DialogTitle>
        <DialogContent dividers>
          {/* Success state */}
          {success ? (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 48, color: "success.main", mb: 1 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>
                Erfolgreich gespeichert!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {entityLabel}-{idLabel} wurde auf <strong>{trimmed}</strong> gesetzt.
              </Typography>
            </Box>
          ) : (
            <>
              {/* Current value display */}
              {currentValue && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Aktuelle {idLabel}: <strong>{currentValue}</strong>
                  </Typography>
                </Box>
              )}

              {/* Preflight warning when changing an existing value */}
              {isChange && (affectedCount === null || (affectedCount ?? 0) > 0) && (
                <Alert
                  severity="warning"
                  icon={<WarningAmberIcon fontSize="inherit" />}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Achtung: Diese Änderung wirkt sich auf bestehende Rezepte aus.
                  </Typography>
                  <Typography variant="body2">
                    {loadingCount ? (
                      <>Betroffene Rezepte werden ermittelt…</>
                    ) : affectedCount === null ? (
                      <>Anzahl betroffener Rezepte konnte nicht ermittelt werden.</>
                    ) : (
                      <>
                        <strong>{affectedCount}</strong> Rezept{affectedCount === 1 ? "" : "e"} {affectedCount === 1 ? "ist" : "sind"} mit diesem {entityLabel} verknüpft und {affectedCount === 1 ? "wird" : "werden"} auf die neue {idLabel} aktualisiert.
                      </>
                    )}
                  </Typography>
                </Alert>
              )}

              {/* Unlock step: when changing, require explicit unlock before showing input */}
              {isChange && !unlocked ? (
                <Box sx={{ mb: 1 }}>
                  <Button
                    onClick={() => {
                      setUnlocked(true);
                      setNewValue(""); // Clear so user must type the new value
                    }}
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={loadingCount}
                  >
                    {idLabel} ändern
                  </Button>
                </Box>
              ) : (
                /* New value input */
                <Box sx={{ mb: 2 }}>
                  <TextField
                    label={`${currentValue ? "Neue" : ""} ${idLabel} (9-stellig)`}
                    value={newValue}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "").slice(0, 9);
                      setNewValue(val);
                    }}
                    placeholder="z.B. 123456789"
                    fullWidth
                    size="small"
                    inputProps={{ maxLength: 9 }}
                    error={newValue.length > 0 && newValue.length < 9}
                    helperText={
                      newValue.length > 0 && newValue.length < 9
                        ? `${newValue.length}/9 Ziffern eingegeben`
                        : ""
                    }
                    disabled={saving}
                    autoFocus
                  />
                </Box>
              )}

              {error && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {error}
                </Typography>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          {success ? (
            <Button onClick={onClose} variant="contained" size="small">
              Schließen
            </Button>
          ) : (
            <>
              <Button onClick={onClose} size="small" disabled={saving}>
                Abbrechen
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                size="small"
                disabled={!isValid || saving || loadingCount || (isChange && !unlocked)}
                startIcon={saving ? <CircularProgress size={14} /> : undefined}
              >
                {saving ? "Speichern…" : isChange ? "Änderung übernehmen" : "Übernehmen"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    );
  }
);

export default IdMigrationDialog;
